import { useEffect, useRef, useState } from 'react';
import { istApp, hatTouch } from '../lib/platform.js';

// Ab hier löst das Loslassen die Aktualisierung aus.
const SCHWELLE = 70;

// Weiter lässt sich nicht ziehen — der Gummiband-Effekt läuft aus.
const MAX_ZUG = 110;

/**
 * Ziehen zum Aktualisieren.
 *
 * Nur in der App aktiv: Android-Browser bringen die Geste selbst mit, und
 * zwei Umsetzungen übereinander fühlen sich kaputt an.
 *
 * Die Geste startet ausschliesslich am oberen Rand der Seite und nur bei
 * eindeutig senkrechter Bewegung — sonst wäre die waagerechte Zeitachse nicht
 * mehr bedienbar.
 */
export default function PullToRefresh({ onRefresh, children }) {
  const [zug, setZug] = useState(0);
  const [busy, setBusy] = useState(false);
  const start = useRef(null);

  const aktiv = istApp() && hatTouch();

  useEffect(() => {
    if (!aktiv) return undefined;

    const beginn = (e) => {
      if (window.scrollY > 0 || busy || e.touches.length !== 1) {
        start.current = null;
        return;
      }
      start.current = { x: e.touches[0].clientX, y: e.touches[0].clientY, entschieden: false };
    };

    const bewegung = (e) => {
      if (!start.current || busy) return;

      const dy = e.touches[0].clientY - start.current.y;
      const dx = e.touches[0].clientX - start.current.x;

      // Richtung einmal festlegen: waagerechte Gesten gehören dem Inhalt.
      if (!start.current.entschieden) {
        if (Math.abs(dx) > Math.abs(dy)) {
          start.current = null;
          return;
        }
        if (Math.abs(dy) < 8) return;
        start.current.entschieden = true;
      }

      if (dy <= 0) {
        setZug(0);
        return;
      }

      // Widerstand: der Zug wird nach hinten heraus zäher.
      const gezogen = Math.min(MAX_ZUG, dy * 0.5);
      setZug(gezogen);

      // Ohne das scrollt der Webview die Seite mit und die Geste bricht ab.
      if (e.cancelable) e.preventDefault();
    };

    const ende = async () => {
      if (!start.current) return;
      const weit = zug >= SCHWELLE;
      start.current = null;

      if (!weit) {
        setZug(0);
        return;
      }

      setBusy(true);
      setZug(SCHWELLE);
      try {
        await onRefresh();
      } finally {
        setBusy(false);
        setZug(0);
      }
    };

    // passive:false, weil bewegung() preventDefault aufrufen muss.
    window.addEventListener('touchstart', beginn, { passive: true });
    window.addEventListener('touchmove', bewegung, { passive: false });
    window.addEventListener('touchend', ende);
    window.addEventListener('touchcancel', ende);

    return () => {
      window.removeEventListener('touchstart', beginn);
      window.removeEventListener('touchmove', bewegung);
      window.removeEventListener('touchend', ende);
      window.removeEventListener('touchcancel', ende);
    };
  }, [aktiv, zug, busy, onRefresh]);

  if (!aktiv) return children;

  const bereit = zug >= SCHWELLE;

  return (
    <>
      <div
        className={'ptr' + (busy ? ' ptr--laeuft' : '')}
        style={{ height: zug, opacity: zug > 0 ? 1 : 0 }}
        aria-hidden={zug === 0}
      >
        <span
          className={'ptr__zeichen' + (bereit || busy ? ' ptr__zeichen--bereit' : '')}
          style={{ transform: `rotate(${Math.min(180, (zug / SCHWELLE) * 180)}deg)` }}
        >
          ↻
        </span>
        <span className="ptr__text">
          {busy ? 'Wird geladen…' : bereit ? 'Loslassen zum Aktualisieren' : 'Zum Aktualisieren ziehen'}
        </span>
      </div>

      <div style={{ transform: `translateY(${zug}px)`, transition: zug === 0 ? 'transform .2s' : 'none' }}>
        {children}
      </div>
    </>
  );
}
